import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { buildDerivedNotifications } from "./notifications/derive";
import {
  ICONS, READ_KEY, readCachedItems, readIds, writeCachedItems,
} from "./notifications/utils";

/**
 * Bell button. Shows the unread badge count and navigates to the
 * standalone `/notifications` page on click. The heavy popover UI
 * that used to live here now lives at `/pages/Notifications.jsx`.
 */
export default function NotificationsBell({ variant = "" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    const cached = readCachedItems(user?.id || "");
    return cached.map((it) => ({ ...it, icon: ICONS[it.iconKey] || Bell }));
  });
  const [readSet, setReadSet] = useState(() => readIds(READ_KEY));
  const tid = variant ? `-${variant}` : "";

  useEffect(() => {
    let mounted = true;
    buildDerivedNotifications({ userId: user?.id })
      .then((list) => {
        if (!mounted) return;
        const hydrated = list.map((it) => ({ ...it, icon: ICONS[it.iconKey] || Bell }));
        setItems(hydrated);
        if (user?.id) writeCachedItems(user.id, hydrated);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [user?.id]);

  // Refresh read set on window focus so navigating back from the
  // /notifications page immediately reflects the newly-read state.
  useEffect(() => {
    const refresh = () => setReadSet(readIds(READ_KEY));
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  const unread = items.filter((it) => !readSet.has(it.id)).length;

  return (
    <button
      type="button"
      aria-label="Notifications"
      data-testid={`notifications-bell-btn${tid}`}
      onClick={() => {
        // Refresh reads on the way in so the destination page shows
        // an up-to-date "New" bucket even after we mark items read
        // through other flows (e.g. following a link inside a row).
        setReadSet(readIds(READ_KEY));
        navigate("/notifications");
      }}
      className="relative w-10 h-10 rounded-full bg-white shadow-soft border-2 border-coral/20 hover:border-coral/50 text-inkdark hover:text-coral transition-colors flex items-center justify-center shrink-0"
    >
      <Bell className="w-4.5 h-4.5" strokeWidth={2.25} />
      {unread > 0 && (
        <span
          data-testid={`notifications-unread-dot${tid}`}
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold flex items-center justify-center shadow-soft"
        >
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </button>
  );
}
